import { Input, Stack, Text } from '@etg/wings';
import { useEffect, useRef, useState } from 'react';
import type { ChangeEvent, KeyboardEvent, ReactElement } from 'react';
import { css, cx } from '@eti/styles';
import { useController, useFormContext, useFormState } from 'react-hook-form';
import type { SearchLocationsQuery } from '@eti/schema-types';
import { useRevolut, useTranslation } from '@eti/providers';
import { screenReadersOnlyStyles } from '../../../../utils/styleUtils';
import { useFetchSearchLocations } from '../../utils/useFetchSearchLocations';
import { eventKeys } from '../../constants/eventKeys';
import ClickAwayListener from '../clickAwayListener/ClickAwayListener';
import type { EventKey } from '../../types/eventKey';
import LocationListBox from './LocationListbox';
import LocationComboboxSuffix from './LocationComboboxSuffix';

const fieldValueNameStyles = css`
  font-size: 1rem;
  inset: 0;
  padding-inline: 2.8125rem 0.75rem;
  place-content: center;
  pointer-events: none;
  position: absolute;
  z-index: 1;
`;

const specialPaddingStyles = (hasIconSuffix: boolean) => css`
  & input {
    ${!hasIconSuffix ? 'padding-inline-end: 1.25rem;' : undefined}
    ${hasIconSuffix ? 'padding-inline-start: 1.25rem;' : undefined}
  }
`;

const locationWrapperStyles = (hasIconSuffix: boolean) => css`
  align-items: center;
  display: flex;
  justify-content: flex-start;
  padding-inline: ${hasIconSuffix ? '1.25rem 3.125rem' : '3.125rem 1.25rem'};
`;

const locationNameStyles = css`
  overflow: hidden;
  padding-inline-end: 5px;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const iataCodeStyles = css`
  margin-top: 0.0625rem;
`;

const errorWrapperStyles = (hasError: boolean) => css`
  margin-top: ${hasError ? '4px' : '0px'};
  transition: margin-top 0.3s ease-in-out;
`;

const inputWrapperStyles = css`
  position: relative;
`;

const formElementStyles = css`
  input {
    padding-inline-end: 2.75rem;
  }
`;

const locationComboboxStyles = css`
  flex: 1;
`;

interface LocationComboboxProps {
  'data-testid'?: string;
  description?: string;
  iconPrefix?: ReactElement;
  iconSuffix?: ReactElement;
  id: string;
  label: string;
  name: string;
  onBlur?: () => void;
  onSwitchDirection?: () => void;
  placeholder?: string;
}

const bracketToDotNotation = (string: string) => {
  return string.replace(/\[(\d+)\]/g, '.$1');
};

const LocationCombobox = ({
  'data-testid': dataTestid,
  description,
  iconPrefix,
  iconSuffix,
  id,
  label,
  name,
  onBlur,
  onSwitchDirection,
  placeholder,
}: LocationComboboxProps) => {
  const { t } = useTranslation();
  const { isRevolut } = useRevolut();

  const ref = useRef<HTMLInputElement | null>(null);
  const { control, getValues, trigger, setFocus } = useFormContext();

  const { errors } = useFormState({ control });
  const { field } = useController({
    control,
    name,
    rules: {
      validate: (searchLocation: SearchLocationsQuery['searchLocations'][number]) => {
        if (!searchLocation) {
          return t('Validation.Required');
        }

        const key = bracketToDotNotation(name.split('.')[0]);
        const { origin, destination } = getValues(key);

        if (origin?.id && destination?.id && origin.id === destination.id) {
          return t('Searchform.ValidationMessages.LocationsMustDiffer');
        }

        return undefined;
      },
    },
  });
  const { getSearchLocations } = useFetchSearchLocations();
  const [isSearchTermEmpty, setIsSearchTermEmpty] = useState(true);
  const [isSearchTermLongEnough, setIsSearchTermLongEnough] = useState(false);
  const [isHighlightDisabled, setIsHighlightDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [navigatedIndex, setNavigatedIndex] = useState(0);
  const [options, setOptions] = useState<SearchLocationsQuery['searchLocations']>([]);
  const [isFocused, setIsFocused] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const isNameEmpty = !field.value?.name;
  const isClearButtonShowing = isFocused && (!isSearchTermEmpty || !isNameEmpty);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const errorMessage = bracketToDotNotation(name)
    .split('.')
    .reduce<any>((acc, key) => acc?.[key], errors)?.message;
  const hasError = Boolean(errorMessage);

  const clearValues = () => {
    setIsSearchTermEmpty(true);
    setIsSearchTermLongEnough(false);
    setHighlightedIndex(0);
    setNavigatedIndex(0);
    setIsLoading(false);
    setOptions([]);
    setInputValue('');
  };

  const handleOnClickAway = () => {
    onBlur?.();
    setIsOpen(false);
    clearValues();
    setIsFocused(false);
  };

  const handleOnFocus = () => {
    setIsOpen(true);
    setIsFocused(true);
  };

  const handleOnClearInput = () => {
    field.onChange(null);
    clearValues();
    setFocus(name);
  };

  const handleOnOptionSelect = (option: SearchLocationsQuery['searchLocations'][number]) => {
    field.onChange(option);
    clearValues();
    setIsOpen(false);
    trigger(name);
  };

  const handleOnKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!Object.values(eventKeys).includes(event.key as EventKey)) {
      return;
    }

    if (event.altKey && event.key === eventKeys.ARROW_DOWN) {
      setIsOpen(true);

      return;
    }

    const previousIndex = highlightedIndex - 1 >= 0 ? highlightedIndex - 1 : options.length - 1;
    const nextIndex = highlightedIndex + 1 <= options.length - 1 ? highlightedIndex + 1 : 0;

    switch (event.key) {
      case eventKeys.ARROW_DOWN:
        event.preventDefault();

        setHighlightedIndex(nextIndex);
        setNavigatedIndex(nextIndex);
        break;
      case eventKeys.ARROW_UP:
        event.preventDefault();

        setHighlightedIndex(previousIndex);
        setNavigatedIndex(previousIndex);
        break;
      case eventKeys.END:
        event.preventDefault();
        if (options.length === 0) {
          return;
        }

        setHighlightedIndex(options.length - 1);
        setNavigatedIndex(options.length - 1);
        break;
      case eventKeys.ENTER:
        event.preventDefault();

        if (isLoading) {
          break;
        }

        setIsOpen(false);
        if (!options[highlightedIndex]) {
          return;
        }

        field.onChange(options[highlightedIndex]);
        clearValues();
        trigger(name);
        break;
      case eventKeys.ESCAPE:
        setIsOpen(false);
        clearValues();
        break;
      case eventKeys.HOME:
        event.preventDefault();
        if (options.length === 0) {
          return;
        }

        setHighlightedIndex(0);
        setNavigatedIndex(0);
        break;
      case eventKeys.TAB:
        if (!options[highlightedIndex]) {
          return;
        }

        field.onChange(options[highlightedIndex]);
        setIsOpen(false);
        setIsFocused(false);
        clearValues();
        trigger(name);
        break;
      default:
        break;
    }
  };

  const handleOnFetchSearchLocations = async (_searchTerm: string) => {
    setIsLoading(true);
    const fetchedSearchLocations = await getSearchLocations(_searchTerm);
    setIsLoading(false);
    setOptions(fetchedSearchLocations);
  };

  const handleOnSearchTermChange = (event: ChangeEvent<HTMLInputElement>) => {
    const targetValue = event.target.value;
    setInputValue(targetValue);

    const isValueLengthValue = targetValue.length >= 2;
    setIsOpen(true);
    setIsSearchTermEmpty(targetValue === '');
    setIsSearchTermLongEnough(isValueLengthValue);

    if (!isValueLengthValue) {
      setIsLoading(false);
      setOptions([]);
      return;
    }

    handleOnFetchSearchLocations(targetValue);
  };

  const handleOnMouseMove = (index: number) => {
    if (isHighlightDisabled) {
      return;
    }

    setHighlightedIndex(index);
  };

  const handleOnMouseLeave = () => {
    if (isHighlightDisabled) {
      return;
    }

    setHighlightedIndex(-1);
  };

  useEffect(() => {
    setIsHighlightDisabled(true);

    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setIsHighlightDisabled(false);
    }, 50);

    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, [highlightedIndex]);

  const composeRefs = (node: HTMLInputElement) => {
    field.ref(node);
    ref.current = node;
  };

  const hasIconSuffix = Boolean(iconSuffix);

  return (
    <ClickAwayListener
      className={locationComboboxStyles}
      isActive={isOpen}
      onClickAway={handleOnClickAway}
    >
      <Stack data-testid={dataTestid} spacing={4}>
        <Input.Label className={isRevolut ? screenReadersOnlyStyles : undefined} htmlFor={id}>
          {label}
        </Input.Label>
        <div className={inputWrapperStyles}>
          <Input
            aria-activedescendant={
              options.length > 0 ? `${id}-option-${highlightedIndex}` : undefined
            }
            aria-autocomplete="none"
            aria-controls={`${id}-listbox`}
            aria-expanded={isOpen}
            aria-label={
              isSearchTermEmpty && Boolean(field.value) ? `${label} ${field.value.name}` : undefined
            }
            aria-required
            className={cx({
              [formElementStyles]: isClearButtonShowing,
              [specialPaddingStyles(hasIconSuffix)]: isRevolut,
            })}
            descriptionId={`description-${id}`}
            hasError={hasError}
            iconPrefix={iconPrefix}
            iconSuffix={iconSuffix}
            id={id}
            onBlur={field.onBlur}
            onChange={handleOnSearchTermChange}
            onFocus={handleOnFocus}
            onKeyDown={handleOnKeyDown}
            placeholder={isNameEmpty ? placeholder : undefined}
            ref={composeRefs}
            // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role
            role="combobox"
            tabIndex={0}
            value={inputValue}
          />
          {isSearchTermEmpty && field.value && (
            <Text
              aria-hidden
              as={isRevolut ? 'div' : 'span'}
              className={cx(fieldValueNameStyles, {
                [locationWrapperStyles(hasIconSuffix)]: isRevolut,
              })}
              data-testid={`${id}-ghost-text`}
            >
              {isRevolut ? (
                <>
                  <Text as="div" className={locationNameStyles} variant="16">
                    {field.value.name}
                  </Text>
                  <Text as="div" className={iataCodeStyles} color="secondary">
                    {field.value.iataCode}
                  </Text>
                </>
              ) : (
                field.value.name
              )}
            </Text>
          )}
          {!isRevolut ? (
            <LocationComboboxSuffix
              isFocused={isFocused}
              isLoading={isLoading}
              isNameEmpty={isNameEmpty}
              isOrigin={name.includes('.origin')}
              isSearchTermEmpty={isSearchTermEmpty}
              onClearInput={handleOnClearInput}
              onSwitchDirection={onSwitchDirection}
            />
          ) : undefined}
        </div>
      </Stack>
      <LocationListBox
        highlightedIndex={highlightedIndex}
        id={id}
        isLoading={isLoading}
        isOpen={isOpen}
        isSearchTermLongEnough={isSearchTermLongEnough}
        navigatedIndex={navigatedIndex}
        onMouseLeave={handleOnMouseLeave}
        onMouseMove={handleOnMouseMove}
        onOptionSelect={handleOnOptionSelect}
        options={options}
        selectedOptionId={field.value?.id}
      />
      <div className={cx(errorWrapperStyles(hasError), { [screenReadersOnlyStyles]: isRevolut })}>
        <Input.Meta error={errorMessage} errorId={id} hasError={hasError} />
      </div>
      <div id={`description-${id}`} style={{ display: 'none' }}>
        {description}
      </div>
    </ClickAwayListener>
  );
};

export default LocationCombobox;
